<!-- Copyright (c) 2020-2021 ForgeRock. All rights reserved.

This software may be modified and distributed under the terms
of the MIT license. See the LICENSE file for details. -->
<template>
  <div>
    <BContainer fluid="true">
      <BRow
        v-if="widgets.length"
        class="mx-4">
        <div
          v-for="(widget, index) in widgets"
          :class="{'col-sm-4': widget.size === 'small', 'col-sm-6': widget.size === 'medium', 'col-sm-12': widget.size === 'large', 'mt-4': true}"
          :key="widget.type + index">
          <Component
            :is="widget.type"
            :user-details="userDetails"
            :details="widget.details" />
        </div>
      </BRow>
      <BJumbotron
        v-else
        class="mt-4 text-center">
        <div class="d-flex justify-content-center mt-3">
          <i
            class="material-icons-outlined mr-4 md-64"
            aria-hidden="true">
            donut_large
          </i>
          <div class="flex-fow-1">
            <h2>{{ $t('pages.dashboard.noWidget') }}</h2>
            <p v-html="$t('pages.dashboard.noWidgetSubText')" />
          </div>
        </div>
      </BJumbotron>
    </BContainer>
    <BContainer v-if="myApplications.length">
      <BCol>
        <section
          data-test-id="my-applications"
          class="my-applications-wrapper">
          <ListGroup
            :title="$t('pages.dashboard.applications.heading')">
            <ul
              class="list-unstyled ml-4 mr-4 mb-4 my-applications-tiles"
              data-test-id="my-applications-list">
              <FrMyApplicationsListItem
                v-for="application in myApplications"
                :key="application.dashboardDisplayName[0]"
                :application-details="application" />
            </ul>
          </ListGroup>
        </section>
      </BCol>
    </BContainer>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import RestMixin from '@forgerock/platform-shared/src/mixins/RestMixin';
import NotificationMixin from '@forgerock/platform-shared/src/mixins/NotificationMixin';
import ListGroup from '@forgerock/platform-shared/src/components/ListGroup/';
import FrMyApplicationsListItem from '@/components/dashboard/applications/MyApplicationsListItem';
import Welcome from './widgets/WelcomeWidget';
import Workflow from './widgets/WorkflowControlWidget';

/**
 * @description Controlling component for the dashboard
 * @event GET
 */
export default {
  name: 'Dashboard',
  mixins: [
    RestMixin,
    NotificationMixin,
  ],
  components: {
    FrMyApplicationsListItem,
    Welcome,
    ListGroup,
    Workflow,
  },
  data() {
    return {
      widgets: [],
      myApplications: [],
    };
  },
  computed: {
    ...mapState({
      userDetails: (state) => state.UserStore,
      workflow: (state) => state.workflow,
      realm: (state) => state.realm,
    }),
  },
  mounted() {
    this.loadWidgets();
    this.loadMyApplications();
  },
  methods: {
    /**
     * @description Loads a list of permitted applications
     * @fires GET realms/root/realms/<current-realm>/dashboard/assigned
     * @return void
     * */
    loadMyApplications() {
      this.getRequestService({
        context: 'AM',
        apiVersion: 'protocol=1.0,resource=1.0',
      })
        .get(`realms/root/realms/${this.realm}/dashboard/assigned`, { withCredentials: true })
        .then(({ data }) => {
          // Alpha sorted by name
          this.myApplications = Object.values(data).sort((a, b) => a.dashboardDisplayName[0].localeCompare(b.dashboardDisplayName[0]));
        })
        .catch((error) => {
          this.showErrorMessage(error, this.$t('pages.dashboard.errorGetApplications'));
        });
    },
    /**
     * @description Provides a list of Widgets from config file ui-dashboard.json
     * @fires GET config/ui/dashboard
     * @return void
     * */
    loadWidgets() {
      this.getRequestService().get('config/ui/dashboard')
        .then(({ data }) => {
          this.widgets = data.dashboard.widgets;

          if (this.workflow) {
            this.widgets.push({
              type: 'Workflow',
              size: 'large',
            });
          }
        })
        .catch((error) => {
          this.displayNotification('IDMMessages', 'error', error.response.data.message);
        });
    },
  },
};
</script>

<style lang="scss" scoped>
  .my-applications {
    &-wrapper {
      background-color: $white;
      border-radius: $border-radius;
      box-shadow: $box-shadow;
    }

    &-tiles {
      display: grid;
      grid-template-columns: 1fr;
      grid-gap: 1.5rem  $grid-gutter-width;
      grid-auto-flow: row dense;

      @include media-breakpoint-up('md') {
        grid-template-columns: repeat(2, 1fr);
      }

      @include media-breakpoint-up('lg') {
        grid-template-columns: repeat(3, minmax(0, 1fr));
      }
    }
  }
</style>
